import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { WidgetDisplayOption, WidgetType, YAxis } from '@constants/widgets';
import { deviceListQuery, groupListQuery } from '@graphql/asset-management';
import {
  getWidgetDataForReportQuery,
  getWidgetDataQuery,
} from '@graphql/dashboard';
import { propertyListQuery } from '@graphql/property';
import { TranslateService } from '@ngx-translate/core';
import { TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { ToastService } from '@services/ui/toast.service';
import { getErrorMessage } from '@utils/errors';
import { Apollo, QueryRef } from 'apollo-angular';
import {
  Device,
  DeviceListDetailsQueryVariables,
  DeviceListQuery,
  GetDashboardsQuery,
  GetWidgetDataForReportQuery,
  GetWidgetDataForReportQueryVariables,
  GetWidgetDataQuery,
  GetWidgetDataQueryVariables,
  GroupListQuery,
  GroupListQueryVariables,
  Maybe,
  PropertyListQuery,
  PropertyListQueryVariables,
} from 'gql/graphql';
import { combineLatest, Subscription } from 'rxjs';

@Component({
  template: '',
  selector: 'base-custom-widget',
  providers: [{ provide: TRANSLATION_PREFIX, useValue: 'dashboards' }],
})
export class CustomWidget implements OnDestroy, OnInit {
  reportParams = input<
    | {
        id: string;
        // properties: string[];
        // fromDate: Date;
        // toDate: Date;
      }
    | undefined
  >(); // NOTE: must be filled if dashboardId is "report"
  dashboardId = input.required<'report' | (string & {})>();
  refreshRateMinutes = input.required<number>(); // minutes
  widget =
    input.required<
      GetDashboardsQuery['getDashboards'][number]['widgets'][number]
    >();
  id = computed(() => this.widget().id);
  display = computed(() => this.widget().display as WidgetDisplayOption);
  properties = signal<PropertyListQuery['propertyList']>([]);
  groups = signal<GroupListQuery['groupList'] | []>([]);
  deviceList = signal<DeviceListQuery['deviceList'] | []>([]);
  data = signal<GetWidgetDataQuery['getWidgetData'] | null>(null);

  loadingWidgetData = signal(false);

  queryRef:
    | QueryRef<
        GetWidgetDataQuery | GetWidgetDataForReportQuery,
        GetWidgetDataQueryVariables | GetWidgetDataForReportQueryVariables
      >
    | undefined;
  protected toast = inject(ToastService);
  protected translate = inject(TranslateService);
  protected apollo = inject(Apollo);
  protected subscriptions: Subscription[] = [];

  constructor() {
    effect(() => {
      if (this.queryRef && this.dashboardId() !== 'report')
        this.queryRef.setOptions({
          pollInterval: this.refreshRateMinutes() * 60 * 1000,
        });
    });
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
  ngOnInit(): void {
    this.loadingWidgetData.set(true);
    if (this.widget().display !== WidgetDisplayOption.RICH_TEXT) {
      this.queryRef =
        // HACK: reuse the dashboard implementation as much as possible
        (
          this.dashboardId() !== 'report'
            ? this.apollo.watchQuery({
                query: getWidgetDataQuery,
                variables: {
                  dashboardId: this.dashboardId(),
                  widgetId: this.id(),
                },
                pollInterval: this.refreshRateMinutes() * 60 * 1000,
              })
            : this.apollo.watchQuery({
                query: getWidgetDataForReportQuery,
                variables: {
                  reportId: this.reportParams()!.id,
                  // properties: this.reportParams()!.properties,
                  // fromDate: this.reportParams()!.fromDate,
                  // toDate: this.reportParams()!.toDate,
                  widgetId: this.id(),
                },
              })
        ) as QueryRef<
          GetWidgetDataQuery | GetWidgetDataForReportQuery,
          GetWidgetDataQueryVariables | GetWidgetDataForReportQueryVariables
        >;

      this.subscriptions.push(
        this.queryRef.valueChanges.subscribe({
          next: ({ data }) => {
            this.data.set(
              'getWidgetData' in data
                ? data.getWidgetData
                : data.getWidgetDataForReport
            );
            this.loadingWidgetData.set(false);
          },
          error: (err) => {
            this.loadingWidgetData.set(false);
          },
        })
      );
    }

    // ENERGY_COST_BREAKDOWN widget: fetch property and group lists
    if (
      this.widget().type === WidgetType.ENERGY_COST_BREAKDOWN ||
      this.widget().type === WidgetType.ACTUAL_VS_TARGET_VALUES
    ) {
     combineLatest([
        this.apollo.watchQuery<PropertyListQuery, PropertyListQueryVariables>({
          query: propertyListQuery
        }).valueChanges,
        this.apollo.watchQuery<GroupListQuery, GroupListQueryVariables>({
          query: groupListQuery
        }).valueChanges,
      ]).subscribe({
        next: ([propertyList, groupList]) => {
          this.properties.set(propertyList.data.propertyList);
          this.groups.set(groupList.data.groupList);
          // Use properties and groups as needed
        },
        error: (err) => {
          // Handle errors if needed
        },
      });
    }
    if (this.widget().type === WidgetType.ACTUAL_VS_TARGET_VALUES) {
      this.apollo
        .watchQuery<DeviceListQuery, DeviceListDetailsQueryVariables>({
          query: deviceListQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.deviceList.set(data.deviceList);
          },
          error: (err) => {
            // this.toast.notifyError(getErrorMessage(err));
          },
        });
    }
  }
}
